import { ReactNode } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import {
  boschBlue,
  boschGray,
  boschGreen,
  boschRed,
  boschYellow,
} from '../../modules/theme/boschColors';

/**
 * Severity drives both the icon (info / warning / error / success) AND
 * the colour palette used for the accent stripe and background tint.
 *
 * We deliberately limit to four values matching the brand guide:
 *
 *   - info    → Bosch Blue (informational, low-stakes notice)
 *   - warning → Bosch Yellow (action required, but no failure)
 *   - error   → Bosch Red (something went wrong / restricted access)
 *   - success → Bosch Green (positive confirmation / completed action)
 *
 * Keep the keys in sync with the `configSchema` option list in
 * `widgets.tsx` so the form dropdown matches what the renderer
 * accepts.
 */
export type AnnouncementSeverity = 'info' | 'warning' | 'error' | 'success';

type Palette = {
  accent: string;
  border: string;
  background: string;
  title: string;
  icon: ReactNode;
};

const PALETTES: Record<AnnouncementSeverity, Palette> = {
  info: {
    accent: boschBlue[50],
    border: boschBlue[80],
    background: boschBlue[95],
    title: boschBlue[20],
    icon: <InfoIcon />,
  },
  warning: {
    accent: boschYellow[80],
    border: boschYellow[85],
    background: boschYellow[95],
    title: boschYellow[50],
    icon: <WarningIcon />,
  },
  error: {
    accent: boschRed[50],
    border: boschRed[85],
    background: boschRed[95],
    title: boschRed[40],
    icon: <ErrorIcon />,
  },
  success: {
    accent: boschGreen[50],
    border: boschGreen[90],
    background: boschGreen[95],
    title: boschGreen[40],
    icon: <CheckCircleIcon />,
  },
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 6,
    border: '1px solid transparent',
    borderLeftWidth: 4,
  },
  iconWrap: {
    flexShrink: 0,
    display: 'flex',
    paddingTop: 2,
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
  },
  text: {
    fontSize: '0.9rem',
    color: boschGray[20],
    lineHeight: 1.5,
  },
}));

export type AnnouncementProps = {
  severity?: AnnouncementSeverity;
  title?: string;
  body?: string;
};

/**
 * Inline status banner ("alert") that admins can use to flag things
 * like maintenance windows, breaking changes, or accomplishments.
 *
 * Deliberately decoupled from the MUI `<Alert>` from `@material-ui/lab`
 * (which we don't ship). Using a hand-rolled component lets us pin the
 * exact Bosch palette and keep the icon set consistent with
 * `pageLayout.tsx` / `KindsGuidePage.tsx`.
 */
export const Announcement = ({
  severity = 'info',
  title,
  body,
}: AnnouncementProps) => {
  const classes = useStyles();
  const palette = PALETTES[severity] ?? PALETTES.info;
  return (
    <div
      className={classes.root}
      style={{
        borderColor: palette.border,
        borderLeftColor: palette.accent,
        backgroundColor: palette.background,
      }}
    >
      <div className={classes.iconWrap} style={{ color: palette.accent }}>
        {palette.icon}
      </div>
      <div className={classes.body}>
        {title && (
          <Typography
            className={classes.title}
            style={{ color: palette.title }}
          >
            {title}
          </Typography>
        )}
        {body && (
          <Typography className={classes.text}>{body}</Typography>
        )}
      </div>
    </div>
  );
};
